import api from './axiosConfig';

export type UserFeedbackSource = 'MAIN' | 'PANEL';
export type UserFeedbackExperience = 'HELPFUL' | 'NEUTRAL' | 'NEEDS_WORK';
export type UserFeedbackContinueUse = 'YES' | 'PRICE_DEPENDENT' | 'NO';
export type UserFeedbackPaidIntent = 'CONSIDER' | 'PRICE_DEPENDENT' | 'FREE_ONLY' | 'UNSURE';
export type UserFeedbackGuestReason =
    | 'NEED_MORE_VALUE'
    | 'NO_REASON_YET'
    | 'PRICE_CONCERN'
    | 'LOGIN_FRICTION'
    | 'OTHER';

export type SubmitUserFeedbackPayload = {
  source: UserFeedbackSource;
  experience: UserFeedbackExperience;
  continueUse: UserFeedbackContinueUse;
  message: string;
  paidIntent?: UserFeedbackPaidIntent;
  guestReason?: UserFeedbackGuestReason;
};

export function feedbackSessionAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.localStorage.getItem('token'));
  } catch {
    return false;
  }
}

function getOrCreateSupportSessionId(): string {
  if (typeof window === 'undefined') return '';
  const key = 'policyIntel:bugReportAnonSession:v1';
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const generated =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(key, generated);
    return generated;
  } catch {
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

const EXPERIENCE_LABEL: Record<UserFeedbackExperience, string> = {
  HELPFUL: '도움 됨',
  NEUTRAL: '보통',
  NEEDS_WORK: '아쉬움',
};

const CONTINUE_LABEL: Record<UserFeedbackContinueUse, string> = {
  YES: '계속 사용할 의향 있음',
  PRICE_DEPENDENT: '기능/가격에 따라',
  NO: '현재는 없음',
};

const PAID_LABEL: Record<UserFeedbackPaidIntent, string> = {
  CONSIDER: '유료 전환 고려',
  PRICE_DEPENDENT: '가격에 따라 고려',
  FREE_ONLY: '무료일 때만 사용',
  UNSURE: '아직 모르겠음',
};

const GUEST_REASON_LABEL: Record<UserFeedbackGuestReason, string> = {
  NEED_MORE_VALUE: '기능을 더 보고 싶음',
  NO_REASON_YET: '가입할 이유가 아직 없음',
  PRICE_CONCERN: '가격이 걱정됨',
  LOGIN_FRICTION: '가입/로그인이 번거로움',
  OTHER: '기타',
};

export async function submitUserFeedback(
    payload: SubmitUserFeedbackPayload,
): Promise<{ success?: boolean; id?: number; status?: string; message?: string }> {
  const authenticated = feedbackSessionAuthenticated();
  const form = new FormData();

  const sourceLabel = payload.source === 'PANEL' ? 'LiveSummary 패널' : '메인';
  const audienceLabel = authenticated ? '로그인 사용자' : '비로그인 사용자';

  const lines = [
    `[피드백 출처] ${sourceLabel}`,
    `[사용자 상태] ${audienceLabel}`,
    `[사용 경험] ${EXPERIENCE_LABEL[payload.experience]}`,
    `[계속 사용할 의향] ${CONTINUE_LABEL[payload.continueUse]}`,
  ];

  if (authenticated) {
    const paidIntent = payload.paidIntent ?? 'UNSURE';
    lines.push(`[유료 전환 의향] ${PAID_LABEL[paidIntent]}`);
  } else {
    const guestReason = payload.guestReason ?? 'NO_REASON_YET';
    lines.push(`[가입하지 않은 이유] ${GUEST_REASON_LABEL[guestReason]}`);
  }

  lines.push('', payload.message.trim());

  form.append('title', `[사용자 피드백][${sourceLabel}][${authenticated ? 'MEMBER' : 'GUEST'}]`);
  form.append('description', lines.join('\n'));
  form.append(
      'pagePath',
      typeof window === 'undefined'
          ? ''
          : `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  form.append('anonSessionId', getOrCreateSupportSessionId());
  form.append('includeTechnicalInfo', 'true');

  if (typeof window !== 'undefined') {
    form.append(
        'browserInfo',
        JSON.stringify({
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          feedbackSource: payload.source,
          clientAuthenticated: authenticated,
        }),
    );
    form.append(
        'frontendVersion',
        String((window as any).__POLICY_INTEL_VERSION__ ?? ''),
    );
  }

  const response = await api.post('/support/feedback', form, {
    headers: {
      'Content-Type': null,
    },
    withCredentials: true,
    timeout: 8_000,
  });

  return response.data ?? {};
}
